import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Plus, PencilSimple, Archive, ArrowCounterClockwise, Users, IdentificationCard, Truck, ShieldWarning, Books, GraduationCap } from "@phosphor-icons/react";

// ---- generic many-to-many link section (people / job roles / asset types / rams / documents)
function LinkSection({ deptId, entityType, title, Icon, items, labelFn, testid }) {
  const [links, setLinks] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback(() => {
    api.get(`/compliance/department-links?department_id=${deptId}&entity_type=${entityType}`)
      .then((r) => setLinks(r.data)).catch(() => setLinks([]));
  }, [deptId, entityType]);
  useEffect(() => { load(); }, [load]);

  const linkFor = (id) => links.find((l) => l.entity_id === id);
  const toggle = async (id) => {
    const ex = linkFor(id);
    try {
      if (ex) await api.delete(`/compliance/department-links/${ex.id}`);
      else await api.post("/compliance/department-links", { department_id: deptId, entity_type: entityType, entity_id: id });
      load();
    } catch (e) { toast.error("Failed to update"); }
  };
  const filtered = items.filter((it) => labelFn(it).toLowerCase().includes(q.toLowerCase()));
  const onCount = links.length;

  const assignAll = async () => {
    const toAdd = filtered.filter((it) => !linkFor(it.id));
    if (toAdd.length === 0) return;
    try {
      await Promise.all(toAdd.map((it) => api.post("/compliance/department-links", { department_id: deptId, entity_type: entityType, entity_id: it.id })));
      toast.success(`Assigned ${toAdd.length}`);
      load();
    } catch (e) { toast.error("Bulk assign failed"); load(); }
  };
  const clearAll = async () => {
    const toRemove = filtered.map((it) => linkFor(it.id)).filter(Boolean);
    if (toRemove.length === 0) return;
    try {
      await Promise.all(toRemove.map((l) => api.delete(`/compliance/department-links/${l.id}`)));
      toast.success(`Removed ${toRemove.length}`);
      load();
    } catch (e) { toast.error("Bulk remove failed"); load(); }
  };

  return (
    <div className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid={`dept-section-${testid}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={18} weight="bold" className="text-slate-600" />
        <h3 className="font-body font-semibold text-slate-800">{title}</h3>
        <span className="text-xs bg-slate-900 text-white rounded-full px-2 py-0.5">{onCount}</span>
      </div>
      <p className="text-slate-400 text-xs mb-2">Click to assign / unassign. Use the filter + bulk buttons to assign many at once. Items can belong to multiple departments.</p>
      <div className="flex gap-2 mb-2">
        <Input data-testid={`dept-search-${testid}`} placeholder="Filter…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 flex-1" />
        <Button data-testid={`dept-assignall-${testid}`} onClick={assignAll} variant="outline" className="h-9 px-2 text-xs font-semibold whitespace-nowrap">Assign all shown</Button>
        <Button data-testid={`dept-clearall-${testid}`} onClick={clearAll} variant="outline" className="h-9 px-2 text-xs font-semibold whitespace-nowrap text-red-600">Clear shown</Button>
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-52 overflow-y-auto">
        {filtered.map((it) => {
          const on = !!linkFor(it.id);
          return (
            <button key={it.id} type="button" data-testid={`dept-${testid}-toggle-${it.id}`} onClick={() => toggle(it.id)}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full border-2 transition-colors duration-100 ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
              {labelFn(it)}
            </button>
          );
        })}
        {filtered.length === 0 && <span className="text-slate-400 text-xs">None.</span>}
      </div>
    </div>
  );
}

// ---- competency section: 3-state (Required for everyone / Relevant / Not set)
function CompetencySection({ deptId, competencies }) {
  const [reqs, setReqs] = useState([]);
  const [links, setLinks] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback(() => {
    api.get(`/compliance/requirements?scope=department&scope_id=${deptId}`).then((r) => setReqs(r.data)).catch(() => setReqs([]));
    api.get(`/compliance/department-links?department_id=${deptId}&entity_type=competency_type`).then((r) => setLinks(r.data)).catch(() => setLinks([]));
  }, [deptId]);
  useEffect(() => { load(); }, [load]);

  const reqFor = (ctid) => reqs.find((r) => r.competency_type_id === ctid);
  const linkFor = (ctid) => links.find((l) => l.entity_id === ctid);
  const stateFor = (ctid) => { const r = reqFor(ctid); return r ? r.level : (linkFor(ctid) ? "optional" : "none"); };

  const setState = async (ctid, level) => {
    try {
      const link = linkFor(ctid);
      const req = reqFor(ctid);
      if (level === "none") {
        if (req) await api.delete(`/compliance/requirements/${req.id}`);
        if (link) await api.delete(`/compliance/department-links/${link.id}`);
      } else {
        await api.post("/compliance/requirements", { scope: "department", scope_id: deptId, competency_type_id: ctid, level });
        if (!link) await api.post("/compliance/department-links", { department_id: deptId, entity_type: "competency_type", entity_id: ctid });
      }
      load();
    } catch (e) { toast.error("Failed to update"); }
  };

  const OPTS = [["required", "Required for all", "bg-red-600"], ["optional", "Relevant", "bg-blue-600"], ["none", "Not set", "bg-slate-400"]];
  const filtered = competencies.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="bg-white border-2 border-slate-200 rounded-lg p-4 sm:col-span-2" data-testid="dept-section-competency">
      <div className="flex items-center gap-2 mb-1">
        <GraduationCap size={18} weight="bold" className="text-slate-600" />
        <h3 className="font-body font-semibold text-slate-800">Competencies & Authorisations</h3>
      </div>
      <p className="text-slate-400 text-xs mb-2">
        <strong>Required for all</strong> = everyone in this department must hold it (missing/expired shows red).
        <strong> Relevant</strong> = shown as relevant/optional, never makes a person red.
        Per-person and per-job-role requirements are set on the person's Compliance screen.
      </p>
      <Input data-testid="dept-search-competency" placeholder="Filter competencies…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 mb-2" />
      <div className="flex flex-col gap-1.5 max-h-72 overflow-y-auto">
        {filtered.map((c) => {
          const st = stateFor(c.id);
          return (
            <div key={c.id} data-testid={`dept-comp-${c.id}`} className="flex items-center gap-2 text-sm border border-slate-100 rounded px-2 py-1.5 flex-wrap">
              <span className="font-body font-semibold text-slate-800">{c.name}</span>
              {c.is_authorisation && <span className="text-[10px] uppercase bg-indigo-100 text-indigo-700 px-1 rounded">Authorisation</span>}
              <div className="ml-auto flex gap-1">
                {OPTS.map(([lvl, lbl, colour]) => (
                  <button key={lvl} type="button" data-testid={`dept-comp-${c.id}-${lvl}`} onClick={() => setState(c.id, lvl)}
                    className={`text-xs font-semibold px-2 py-1 rounded border-2 ${st === lvl ? `${colour} text-white border-transparent` : "bg-white text-slate-500 border-slate-300 hover:border-slate-500"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <span className="text-slate-400 text-xs">No competencies.</span>}
      </div>
    </div>
  );
}

export default function DepartmentsPage() {
  const { hasPerm } = useAuth();
  const isAdmin = hasPerm("administrator");
  const [depts, setDepts] = useState([]);
  const [archivedDepts, setArchivedDepts] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  // entity catalogs
  const [people, setPeople] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [types, setTypes] = useState([]);
  const [rams, setRams] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [competencies, setCompetencies] = useState([]);

  const loadDepts = useCallback(() => {
    api.get("/config/departments").then((r) => { setDepts(r.data); setSelected((s) => s || (r.data[0]?.id ?? null)); }).catch(() => {});
    api.get("/config/departments?include_archived=true").then((r) => setArchivedDepts(r.data.filter((d) => d.archived_at))).catch(() => {});
  }, []);
  useEffect(() => { loadDepts(); }, [loadDepts]);
  useEffect(() => {
    api.get("/people").then((r) => setPeople(r.data)).catch(() => {});
    api.get("/config/job-titles").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/config/asset-types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/rams").then((r) => setRams(r.data)).catch(() => {});
    api.get("/documents").then((r) => setDocuments(r.data)).catch(() => {});
    api.get("/config/competency-types").then((r) => setCompetencies(r.data)).catch(() => {});
  }, []);

  const createDept = async () => {
    if (!newName.trim()) return;
    try { const r = await api.post("/config/departments", { name: newName.trim() }); setNewName(""); loadDepts(); setSelected(r.data.id); toast.success("Department created"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const rename = async (id) => {
    if (!editName.trim()) return;
    try { await api.put(`/config/departments/${id}`, { name: editName.trim() }); setEditingId(null); loadDepts(); toast.success("Renamed"); }
    catch (e) { toast.error("Failed"); }
  };
  const archive = async (id) => {
    if (!window.confirm("Archive this department? Its relationships are kept and it can be restored.")) return;
    try { await api.post(`/config/departments/${id}/archive`); if (selected === id) setSelected(null); loadDepts(); toast.success("Archived"); }
    catch (e) { toast.error("Failed"); }
  };
  const restore = async (id) => { try { await api.post(`/config/departments/${id}/restore`); loadDepts(); toast.success("Restored"); } catch (e) { toast.error("Failed"); } };

  const sel = depts.find((d) => d.id === selected);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Departments</h1>
        <p className="font-body text-slate-600 text-sm">Assign people, job roles, asset types, RAMS/policies, documents and competencies to each department in one place. Relevance filtering and department compliance boards use these links.</p>
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-5">
        {/* Left: department list */}
        <div className="flex flex-col gap-2">
          <div className="bg-white border-2 border-slate-200 rounded-lg p-3">
            <h3 className="font-body font-semibold text-slate-800 text-sm mb-2">Your departments</h3>
            <div className="flex flex-col gap-1">
              {depts.map((d) => (
                <div key={d.id} className="flex items-center gap-1">
                  {editingId === d.id ? (
                    <>
                      <Input data-testid={`dept-edit-input-${d.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" />
                      <Button data-testid={`dept-save-${d.id}`} onClick={() => rename(d.id)} className="h-8 px-2 bg-slate-900 font-semibold text-xs">Save</Button>
                    </>
                  ) : (
                    <>
                      <button data-testid={`dept-select-${d.id}`} onClick={() => setSelected(d.id)}
                        className={`flex-1 text-left text-sm font-semibold px-3 py-2 rounded ${selected === d.id ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"}`}>{d.name}</button>
                      {isAdmin && <button data-testid={`dept-rename-${d.id}`} onClick={() => { setEditingId(d.id); setEditName(d.name); }} className="text-slate-400 hover:text-slate-700 p-1"><PencilSimple size={15} weight="bold" /></button>}
                      {isAdmin && <button data-testid={`dept-archive-${d.id}`} onClick={() => archive(d.id)} className="text-slate-400 hover:text-red-600 p-1"><Archive size={15} weight="bold" /></button>}
                    </>
                  )}
                </div>
              ))}
              {depts.length === 0 && <span className="text-slate-400 text-xs">No departments yet.</span>}
            </div>
            {isAdmin && (
              <div className="flex gap-1 mt-3 border-t border-slate-100 pt-3">
                <Input data-testid="dept-new-name" placeholder="New department" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createDept()} className="h-9" />
                <Button data-testid="dept-create" onClick={createDept} className="h-9 px-2 bg-slate-900 font-semibold"><Plus size={16} weight="bold" /></Button>
              </div>
            )}
          </div>

          {archivedDepts.length > 0 && (
            <div className="bg-white border-2 border-slate-200 rounded-lg p-3">
              <button data-testid="dept-toggle-archived" onClick={() => setShowArchived((s) => !s)} className="text-xs font-bold uppercase text-slate-500 inline-flex items-center gap-1">
                <Archive size={13} weight="bold" />Archived ({archivedDepts.length}) {showArchived ? "▾" : "▸"}
              </button>
              {showArchived && (
                <div className="flex flex-col gap-1 mt-2">
                  {archivedDepts.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 text-sm">
                      <span className="flex-1 text-slate-500 line-through">{d.name}</span>
                      {isAdmin && <button data-testid={`dept-restore-${d.id}`} onClick={() => restore(d.id)} className="text-emerald-700 font-bold inline-flex items-center gap-1 text-xs"><ArrowCounterClockwise size={14} weight="bold" />Restore</button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: sections for selected department */}
        {sel ? (
          <div className="flex flex-col gap-4" data-testid="dept-detail">
            <h2 className="font-display text-xl font-black text-slate-900">{sel.name}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <LinkSection deptId={sel.id} entityType="profile" title="People" Icon={Users} testid="people"
                items={people} labelFn={(p) => `${p.first_name} ${p.last_name}`} />
              <LinkSection deptId={sel.id} entityType="job_title" title="Job roles" Icon={IdentificationCard} testid="job"
                items={jobs} labelFn={(j) => j.name} />
              <LinkSection deptId={sel.id} entityType="asset_type" title="Asset types" Icon={Truck} testid="assettype"
                items={types} labelFn={(t) => t.name} />
              <LinkSection deptId={sel.id} entityType="rams" title="RAMS / Policies" Icon={ShieldWarning} testid="rams"
                items={rams} labelFn={(r) => `${r.ref ? r.ref + " " : ""}${r.title}`} />
              <LinkSection deptId={sel.id} entityType="document" title="Documents" Icon={Books} testid="document"
                items={documents} labelFn={(d) => d.title} />
              <CompetencySection deptId={sel.id} competencies={competencies} />
            </div>
          </div>
        ) : (
          <div className="text-slate-400 flex items-center justify-center border-2 border-dashed border-slate-200 rounded-lg p-10">Select or create a department.</div>
        )}
      </div>
    </div>
  );
}
